import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type QrScanner from 'qr-scanner';
import { ArrowLeft } from 'lucide-react';
import { parseEviQr } from '@/lib/qr';
import { scanDestination } from '@/lib/qrScanDestination';
import { toast } from '@/store/toast';
import styles from './ScanPage.module.css';

interface Props { onNavigate: (path: string) => void }

type CameraState = 'starting' | 'scanning' | 'scanned' | 'denied' | 'unsupported';

export function ScanPage({ onNavigate }: Props) {
  const { t } = useTranslation('scan');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  // One-shot: the first recognised code wins — guards the decode loop (and
  // a simulator tap) from firing navigation twice.
  const handledRef = useRef(false);
  // Throttle the "unrecognised" toast so a non-EVI code held in frame
  // (qr-scanner re-fires every decode) doesn't spam.
  const lastRejectRef = useRef(0);
  const [camera, setCamera] = useState<CameraState>('starting');
  // Fob self-registration mode (/scan?for=fob). Read ONCE at mount: the
  // decode callback is captured inside a mount-only effect, so a per-render
  // read would be stale there. Default mode is the charge scanner, unchanged.
  const fobModeRef = useRef(new URLSearchParams(window.location.search).get('for') === 'fob');
  const recoveryRef = useRef(new URLSearchParams(window.location.search).get('recover'));
  const mode: 'charge' | 'fob' = fobModeRef.current ? 'fob' : 'charge';

  const teardownScanner = (): void => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { s.stop(); } catch { /* noop */ }
      try { s.destroy(); } catch { /* noop */ }
    }
  };

  /**
   * Route a decoded payload (camera OR simulator) through the SHARED
   * parse + navigation path. Returns true for a recognised EVI code. On
   * the first recognised code: tear the camera down BEFORE navigating so
   * it never keeps running, then hand off to the pre-session page.
   */
  const handleDecoded = (payload: string): boolean => {
    const parsed = parseEviQr(payload);
    const dest = scanDestination(parsed, fobModeRef.current ? 'fob' : 'charge');
    if (dest.kind === 'stationOnly' || (recoveryRef.current && parsed.kind !== 'stall')) {
      const now = Date.now();
      if (now - lastRejectRef.current > 3000) {
        lastRejectRef.current = now;
        toast(t('toast.stationOnly'), { tone: 'warn' });
      }
      return false;
    }
    if (dest.kind === 'none') {
      const now = Date.now();
      if (now - lastRejectRef.current > 3000) {
        lastRejectRef.current = now;
        toast(t('toast.unrecognized'), { tone: 'warn' });
      }
      return false;
    }
    if (handledRef.current) return true;
    handledRef.current = true;
    setCamera('scanned');
    teardownScanner();
    // Brief beat so SCANNED reads as a scan rather than an instant teleport.
    const next = dest.path + (recoveryRef.current ? `${dest.path.includes('?') ? '&' : '?'}recover=${encodeURIComponent(recoveryRef.current)}` : '');
    window.setTimeout(() => onNavigate(next), 350);
    return true;
  };

  // ── Camera lifecycle ──────────────────────────────────────────────────
  // qr-scanner (Nimiq) owns getUserMedia + the worker-based decode loop.
  // Lazy-loaded by dynamic import so the decoder + its worker only ship on
  // /scan, staying off the main bundle. Released fully on unmount.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // No camera API at all — older iOS standalone PWA (< 14.3), an
      // insecure context, or a locked-down browser. Show a graceful
      // message, not a dead viewfinder.
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setCamera('unsupported');
        return;
      }
      try {
        const { default: QrScannerCtor } = await import('qr-scanner');
        if (cancelled) return;
        if (!(await QrScannerCtor.hasCamera())) {
          if (!cancelled) setCamera('unsupported');
          return;
        }
        const video = videoRef.current;
        if (!video || cancelled) return;

        const scanner = new QrScannerCtor(
          video,
          (result: { data: string }) => { handleDecoded(result.data); },
          {
            preferredCamera: 'environment',
            maxScansPerSecond: 5,
            highlightScanRegion: false,
            highlightCodeOutline: false,
            returnDetailedScanResult: true,
          },
        );
        scannerRef.current = scanner;
        await scanner.start(); // triggers the OS camera-permission prompt
        if (cancelled) { teardownScanner(); return; }
        setCamera('scanning');
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        // NotAllowedError / SecurityError → the user denied, or insecure
        // context; anything else → treat as unavailable.
        setCamera(
          name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unsupported',
        );
      }
    })();

    return () => {
      cancelled = true;
      teardownScanner();
    };
  }, []);

  const statusLabel =
    camera === 'scanned' ? t('status.scanned')
      : camera === 'scanning' ? t('status.aligning')
        : camera === 'starting' ? t('status.starting')
          : camera === 'denied' ? t('status.cameraOff')
            : t('status.noCamera');

  return (
    <div className={styles.page}>
      {/* ONE glass card carries the whole scan section — title, viewfinder
          and the fallback link — so it reads as a single object floating on
          the shell's ambient bloom rather than three stacked strips bleeding
          into the page edges. `.page` centres it in the band between the
          global top bar and the floating pill. */}
      <section className={styles.card}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('a11y.back')}
          onClick={() => onNavigate(mode === 'fob' ? '/my-fobs' : '/map')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{mode === 'fob' ? t('fobMode.title') : t('title')}</h1>
          <p className={styles.subtitle}>
            {mode === 'fob' ? t('fobMode.subtitle') : t('subtitle')}
          </p>
        </div>
      </header>

      <div className={styles.viewfinder}>
        {/* Placeholder stripes behind the feed — visible during startup and
            on the no-camera states; the live video covers them when ready. */}
        <div className={styles.viewfinderInner} aria-hidden />
        <video
          ref={videoRef}
          className={styles.video}
          data-live={camera === 'scanning' || camera === 'scanned'}
          muted
          playsInline
          aria-hidden
        />
        <CornerMarker corner="tl" />
        <CornerMarker corner="tr" />
        <CornerMarker corner="bl" />
        <CornerMarker corner="br" />
        <span className={styles.aligning}>{statusLabel}</span>
      </div>

      {(camera === 'denied' || camera === 'unsupported') && (
        <p className={styles.helpText}>
          {camera === 'denied'
            ? t('help.denied')
            : t('help.unsupported')}
          <button type="button" className={styles.linkBtn} onClick={() => onNavigate('/map')}>
            {t('help.enterCodeLink')}
          </button>
          .
        </p>
      )}

      <p className={styles.helpText}>
        {t('help.trouble')}{' '}
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => onNavigate('/map')}
        >
          {t('help.enterCodeButton')}
        </button>
        .
      </p>
      </section>
    </div>
  );
}

// ── Inline corner marker (scoped to ScanPage; not exported) ────────────
function CornerMarker({ corner }: { corner: 'tl' | 'tr' | 'bl' | 'br' }) {
  const SIZE = 28;
  const T = 3;
  const R = 10;
  const variants: Record<typeof corner, React.CSSProperties> = {
    tl: { top: 0, left: 0, borderTopWidth: T, borderLeftWidth: T, borderTopLeftRadius: R },
    tr: { top: 0, right: 0, borderTopWidth: T, borderRightWidth: T, borderTopRightRadius: R },
    bl: { bottom: 0, left: 0, borderBottomWidth: T, borderLeftWidth: T, borderBottomLeftRadius: R },
    br: { bottom: 0, right: 0, borderBottomWidth: T, borderRightWidth: T, borderBottomRightRadius: R },
  };
  return (
    <span
      aria-hidden
      className={styles.cornerMarker}
      style={{ width: SIZE, height: SIZE, ...variants[corner] }}
    />
  );
}
